import { FC, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Paper,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useEvaluationFrameworkHook,
  useDeleteEvaluationFrameworkHook,
} from "../../../hooks/useAssessmentHook";
import {
  TEvaluationFramework,
  TEvaluationModel,
  TEvaluationFrequency,
} from "../../../apis/assessment/assessment.interfaces";
import { logger, pages } from "../../../utils/common";
import { useClassHook, useSubjectsHook } from "../../../hooks/useAcademicHook";
import { hasAssessmentPrerequisites } from "./assessment.helpers";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ManageEvaluationFrameworkDialog from "./manageEvaluationFrameworkDialog";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

const EvaluationFrameworkView: FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useEvaluationFrameworkHook();
  const {
    data: classes,
    isLoading: classesLoading,
    error: classesError,
  } = useClassHook();
  const {
    data: subjects,
    isLoading: subjectsLoading,
    error: subjectsError,
  } = useSubjectsHook();
  const deleteFrameworkMutation = useDeleteEvaluationFrameworkHook();
  const { control, watch } = useForm<{ search: string }>({
    defaultValues: { search: "" },
  });
  const searchQuery = watch("search");
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [editingFramework, setEditingFramework] =
    useState<TEvaluationFramework | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [frameworkToDelete, setFrameworkToDelete] =
    useState<TEvaluationFramework | null>(null);

  const prerequisitesMet = useMemo(
    () => hasAssessmentPrerequisites(classes, subjects),
    [classes, subjects],
  );
  const isLoadingPrerequisites = classesLoading || subjectsLoading;
  const hasPrerequisitesError = classesError || subjectsError;

  const filteredFrameworks = useMemo(() => {
    if (!data) return [];

    return data.filter((framework) => {
      const matchesSearch =
        framework.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        framework.description
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        framework.className?.some((name) =>
          name.toLowerCase().includes(searchQuery.toLowerCase()),
        ) ||
        framework.assessmentComponents.some((comp) =>
          comp.name.toLowerCase().includes(searchQuery.toLowerCase()),
        );

      return matchesSearch;
    });
  }, [data, searchQuery]);

  const getModelLabel = (model: TEvaluationModel) => {
    const labels: Record<TEvaluationModel, string> = {
      "semester-based": "Semester-Based",
      annual: "Annual",
      "continuous-evaluation": "Continuous Evaluation",
    };
    return labels[model] || model;
  };

  const getFrequencyLabel = (frequency: TEvaluationFrequency) => {
    const labels: Record<TEvaluationFrequency, string> = {
      weekly: "Weekly",
      "bi-weekly": "Bi-Weekly",
      monthly: "Monthly",
      quarterly: "Quarterly",
      semesterly: "Semesterly",
      annually: "Annually",
    };
    return labels[frequency] || frequency;
  };

  const handleAddFramework = () => {
    setEditingFramework(null);
    setManageDialogOpen(true);
  };

  const handleEditFramework = (framework: TEvaluationFramework) => {
    setEditingFramework(framework);
    setManageDialogOpen(true);
  };

  const handleDeleteFramework = (framework: TEvaluationFramework) => {
    setFrameworkToDelete(framework);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!frameworkToDelete) return;

    try {
      await deleteFrameworkMutation.mutateAsync(frameworkToDelete.id);
      logger.show_success("Evaluation framework deleted successfully!");
      setDeleteDialogOpen(false);
      setFrameworkToDelete(null);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to delete evaluation framework. Please try again.",
      );
    }
  };

  const handleManageDialogClose = () => {
    setManageDialogOpen(false);
    setEditingFramework(null);
  };

  if (isLoading) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Evaluation Framework`}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="evaluation-framework-view"
        >
          <CircularProgress data-testid="evaluation-framework-loading-spinner" />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Evaluation Framework`}
        />
        <Box p={3} data-testid="evaluation-framework-view">
          <Alert severity="error" data-testid="evaluation-framework-error">
            Failed to load evaluation frameworks. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  if (hasPrerequisitesError) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Evaluation Framework`}
        />
        <Box p={3} data-testid="evaluation-framework-view">
          <Alert
            severity="error"
            data-testid="evaluation-framework-prerequisites-error"
          >
            Failed to load classes or subjects data. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  if (isLoadingPrerequisites) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Evaluation Framework`}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="evaluation-framework-view"
        >
          <CircularProgress data-testid="evaluation-framework-loading-spinner" />
        </Box>
      </>
    );
  }

  const showPrerequisitesEmptyState =
    !prerequisitesMet && (!data || data.length === 0);

  return (
    <>
      <DocumentTitle
        title={`${CONSTANTS.title.assessment} - Evaluation Framework`}
      />

      <Grid container spacing={3} data-testid="evaluation-framework-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="evaluation-framework-header"
          >
            <Typography component="h2" variant="h6" fontWeight={600}>
              Evaluation framework
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddFramework}
              size="large"
              disabled={!prerequisitesMet}
              data-testid="add-evaluation-framework-button"
            >
              Add Evaluation Framework
            </Button>
          </Box>
        </Grid>

        {showPrerequisitesEmptyState ? (
          <Grid item xs={12}>
            <Card data-testid="evaluation-framework-prerequisites-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="300px"
                  gap={2}
                >
                  <SchoolIcon sx={{ fontSize: 64, color: "text.secondary" }} />
                  <Alert
                    severity="info"
                    icon={<InfoIcon />}
                    sx={{ width: "100%", maxWidth: 600 }}
                    data-testid="evaluation-framework-prerequisites-alert"
                  >
                    <Typography variant="h6" gutterBottom>
                      Prerequisites Required
                    </Typography>
                    <Typography variant="body2" paragraph>
                      To configure evaluation frameworks, you must first set up
                      classes and subjects in Academics.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate(`${pages.academics.path}/subjects`)}
                      sx={{ mt: 2 }}
                      startIcon={<SchoolIcon />}
                      data-testid="navigate-to-academic-button"
                    >
                      Go to Academic Module
                    </Button>
                  </Alert>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <>
            {/* Search */}
            <Grid item xs={12}>
              <Card data-testid="evaluation-framework-filters-card">
                <CardContent>
                  <ControlledTextField
                    control={control}
                    name="search"
                    id="evaluation-framework-search"
                    label="Search"
                    isGridItem={false}
                    fullWidth
                    placeholder="Search by name, description, class, or component..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ "data-testid": "evaluation-framework-search-input" }}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Framework List */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                data-testid="evaluation-frameworks-count"
              >
                Evaluation Frameworks ({filteredFrameworks.length})
              </Typography>
            </Grid>

            {filteredFrameworks.length === 0 ? (
              <Grid item xs={12}>
                <Card data-testid="evaluation-framework-empty-state">
                  <CardContent>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      minHeight="200px"
                      gap={2}
                    >
                      <AssessmentIcon
                        sx={{ fontSize: 64, color: "text.secondary" }}
                      />
                      <Typography variant="h6" color="text.secondary">
                        {data?.length === 0
                          ? "No evaluation frameworks configured"
                          : "No evaluation frameworks match your search criteria"}
                      </Typography>
                      {data?.length === 0 && prerequisitesMet && (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            Get started by adding your first evaluation
                            framework.
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddFramework}
                            data-testid="add-evaluation-framework-empty-button"
                          >
                            Add Evaluation Framework
                          </Button>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TableContainer
                  component={Paper}
                  data-testid="evaluation-framework-table-container"
                >
                  <Table data-testid="evaluation-framework-table">
                    <TableHead data-testid="evaluation-framework-table-head">
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Classes</TableCell>
                        <TableCell>Evaluation Model</TableCell>
                        <TableCell>Frequency</TableCell>
                        <TableCell>Assessment Components</TableCell>
                        <TableCell>Grade Levels</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody data-testid="evaluation-framework-table-body">
                      {filteredFrameworks.map((framework) => (
                        <TableRow
                          key={framework.id}
                          hover
                          data-testid={`evaluation-framework-row-${framework.id}`}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body1" fontWeight="medium">
                                {framework.name}
                              </Typography>
                              {framework.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {framework.description}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {framework.className &&
                              framework.className.length > 0 ? (
                                framework.className.map((name, idx) => (
                                  <Chip
                                    key={idx}
                                    label={name}
                                    variant="outlined"
                                    size="small"
                                    data-testid={`class-chip-${framework.id}-${idx}`}
                                  />
                                ))
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  N/A
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getModelLabel(framework.evaluationModel)}
                              variant="outlined"
                              size="small"
                              data-testid={`model-chip-${framework.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {getFrequencyLabel(framework.evaluationFrequency)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {framework.assessmentComponents.map((comp) => (
                                <Chip
                                  key={comp.id}
                                  label={`${comp.name}${comp.weightPercentage ? ` (${comp.weightPercentage}%)` : ""}`}
                                  variant="outlined"
                                  size="small"
                                  data-testid={`component-chip-${comp.id}`}
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {framework.gradeLevelNames &&
                              framework.gradeLevelNames.length > 0 ? (
                                framework.gradeLevelNames.map((name, idx) => (
                                  <Chip
                                    key={idx}
                                    label={name}
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    data-testid={`grade-level-chip-${framework.id}-${idx}`}
                                  />
                                ))
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  N/A
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleEditFramework(framework)}
                              color="primary"
                              data-testid={`edit-evaluation-framework-${framework.id}`}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteFramework(framework)}
                              color="error"
                              data-testid={`delete-evaluation-framework-${framework.id}`}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <ManageEvaluationFrameworkDialog
        open={manageDialogOpen}
        onClose={handleManageDialogClose}
        framework={editingFramework}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setFrameworkToDelete(null);
        }}
        data-testid="delete-evaluation-framework-dialog"
      >
        <DialogTitle data-testid="delete-evaluation-framework-dialog-title">
          Delete Evaluation Framework
        </DialogTitle>
        <DialogContent data-testid="delete-evaluation-framework-dialog-content">
          <DialogContentText>
            Are you sure you want to delete evaluation framework "
            {frameworkToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions data-testid="delete-evaluation-framework-dialog-actions">
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setFrameworkToDelete(null);
            }}
            disabled={deleteFrameworkMutation.isPending}
            data-testid="cancel-delete-evaluation-framework-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteFrameworkMutation.isPending}
            startIcon={
              deleteFrameworkMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-delete-evaluation-framework-button"
          >
            {deleteFrameworkMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EvaluationFrameworkView;
